import { Box, Button, Card, Collapse, IconButton, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { Icons, attachmentThumbnails, getFileSizeFromBytes } from '../../utils/utilities'
import materialUploadImg from '../../assets/upload-assignment.svg'
import CancelIcon from '@mui/icons-material/Cancel'
import Progress from '../common/Progress'
import { useRef, useState } from 'react'
import { toast } from 'react-toastify'

const studyMaterialFormats = ['.pdf', '.docx', '.ppsx', '.pptx', '.odp']

const FileInput = ( { setFieldValue, size, selectedAttachments, setSelectedAttachments, isUploading, setAttachmentsNames, attachmentsNames, setAttachmentsSizes, attachmentsSizes, progress, acceptableFormats = ".pdf,.pptx,.ppt,.odp,.ppsx,.docx,.doc" } ) => {

    const [dragOverStatus, setDragOverStatus] = useState( false )
    const { palette, border, theme } = useTheme()

    const fileInputRef = useRef()

    const handleFilesInsert = ( e ) => {
        const files = e.target.files
        let selectedFileList = []
        let selectedFileNameList = []
        let selectedFileSizeList = []
        let unsupportedFiles = []
        for ( let i = 0; i < files.length; i++ ) {
            const file = files[i]
            if ( !studyMaterialFormats.includes( `.${file.name?.split( "." ).pop()}` ) ) {
                unsupportedFiles.push( file.name )
                continue
            }
            selectedFileList.push( file )
            selectedFileNameList.push( file.name.split( "." ).slice( 0, -1 ).join( "" ) )
            selectedFileSizeList.push( file.size )
        }
        if ( selectedFileList.length > 0 ) {
            setSelectedAttachments( [...selectedAttachments, ...selectedFileList] )
            setAttachmentsNames( [...attachmentsNames, ...selectedFileNameList] )
            setAttachmentsSizes( [...attachmentsSizes, ...selectedFileSizeList] )
            setFieldValue( 'attachments', [...selectedAttachments, ...selectedFileList] )
        }
        if ( unsupportedFiles.length > 0 )
            toast( <p>Found unsupported file(s): <strong><em>{unsupportedFiles.join( "," )}</em></strong></p>, )
        e.target.value = ''
    }

    const handleFileNameChange = ( index, value ) => {
        let values = [...attachmentsNames]
        values[index] = value
        setAttachmentsNames( values )
    }

    const removeAttachment = ( index ) => {
        const files = [...selectedAttachments]
        const filesNames = [...attachmentsNames]
        const sizes = [...attachmentsSizes]
        files.splice( index, 1 )
        filesNames.splice( index, 1 )
        sizes.splice( index, 1 )
        setSelectedAttachments( files )
        setAttachmentsNames( filesNames )
        setAttachmentsSizes( sizes )
        setFieldValue( 'attachments', [...files] )
    }

    return (
        <Box>
            <Box
                onDragLeave={() => setDragOverStatus( false )}
                onDrop={() => setDragOverStatus( false )}
                onDragEnter={() => setDragOverStatus( true )}
                onDragOver={() => setDragOverStatus( true )}
                position="relative"
                borderRadius="5px"
                gap="10px"
                display="flex"
                padding="20px"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="150px"
                border={`2px dashed ${dragOverStatus ? palette.text.secondary : palette.borderColor}`}
                bgcolor={palette.contentBg}
                sx={{
                    ":hover": {
                        borderColor: palette.text.secondary
                    }
                }}
            >
                <input ref={fileInputRef} accept={acceptableFormats} disabled={isUploading} style={{ position: "absolute", cursor: isUploading ? "not-allowed" : "pointer", opacity: 0, top: 0, left: 0, width: "100%", height: "100%" }} title="" type="file" multiple name="attachments" id="attachments" onInput={( e ) => { handleFilesInsert( e ) }} />
                {isUploading && <Progress progress={progress} size={size} />}
                {<Box display="flex" flexDirection="column" gap="10px" alignItems="center">
                    <Box component="img" src={materialUploadImg} width="180px" alt="Upload materials" />
                    {!dragOverStatus && !isUploading && <Typography variant='subtitle2'>{selectedAttachments.length > 0 ? "Drag and drop more files" : "Drag and drop to upload attachments"} </Typography>}
                    {!dragOverStatus && !isUploading && <Button onClick={() => fileInputRef.current?.click()} variant='contained' disableElevation color="customThemeColor"  >
                        Browse Files
                    </Button>}
                    {!dragOverStatus && !isUploading && <Typography color='textSecondary' fontSize="14px" textAlign="center">Accepted formats: {studyMaterialFormats.join( ", " )} </Typography>}
                    {dragOverStatus && <Typography sx={{ pointerEvents: "none" }} className='animation-shake' fontWeight="600" variant='body1'>Drop your materials here!</Typography>}
                    {dragOverStatus && <Typography variant="subtitle2" color="textSecondary" fontSize="12px">Supported formats: {studyMaterialFormats.join( ", " )}</Typography>}
                </Box>}
                {isUploading && <Typography variant="body2" fontWeight="bold" color="textSecondary">Uploading selected attachments. This could take a moment...</Typography>}
            </Box>
            <Collapse in={selectedAttachments.length > 0} sx={{ marginTop: "20px" }} >
                <Box >
                    <Typography variant='subtitle2' display="block" component="p" gutterBottom>Selected Attachments</Typography>
                    <Box gap="10px" display="flex" alignItems="center" flexWrap="wrap">
                        {selectedAttachments.length > 0 && selectedAttachments.map( ( attachment, index ) => {
                            const ext = attachment.name?.split( "." ).pop()

                            return (
                                <Card elevation={0} key={index} sx={{ position: "relative", padding: "0", border: theme === 'dark' && border[1], flexGrow: { md: "initial", xs: 1 } }}>
                                    <Box display="flex" gap="10px" alignItems="center" padding="10px" >
                                        <Box width="60px" sx={{ aspectRatio: 1 }} borderRadius="5px" bgcolor={palette.contentBg} display="flex" alignItems="center" justifyContent="center">
                                            {/* {Icons.default.FileIcon} */}
                                            <Box component={"img"} sx={{ objectFit: "cover" }} width="45px" src={attachmentThumbnails[`${ext?.toLowerCase()}`] ?? attachmentThumbnails["default"]} alt={ext} />
                                        </Box>
                                        <Box flexGrow={1} display="flex" gap="5px" flexDirection="column">
                                            <TextField disabled={isUploading} onFocus={e => e.target.select()} size="small" placeholder='Enter file name here...' onChange={( e ) => handleFileNameChange( index, e.target.value )} value={attachmentsNames[index]} sx={{ width: "calc(100% - 30px)" }} slotProps={{
                                                htmlInput: { sx: { fontSize: "14px" } }
                                            }} />
                                            <Box display="flex" justifyContent="space-between" gap="20px" alignItems="center">
                                                <Typography color="GrayText" fontSize="10px"> {attachment.name}</Typography>
                                                <Typography color="GrayText" fontSize="10px">{getFileSizeFromBytes( attachment.size )}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Tooltip title={`Remove ${attachment.name}`} sx={{ zIndex: "10", position: "absolute", top: "0", right: "0" }} placement="top">
                                        <IconButton disabled={isUploading} onClick={() => { removeAttachment( index ) }} size='small'>
                                            <CancelIcon sx={{ color: palette.primary.light + "55", "&:hover": { color: palette.primary.light + "88" } }} />
                                        </IconButton>
                                    </Tooltip>
                                </Card>
                            )
                        } )}
                    </Box>
                </Box>
            </Collapse >
        </Box >
    )
}

export default FileInput
